/**
 * Per-radiologist template customization page.
 *
 * The rad freely edits their template structure inside a TipTap surface
 * (the same node schema the real reporter uses, so chips render the same).
 * Saving walks the doc, emits the complete `sections` list, POSTs to
 * `/api/my-templates`. Routing (which standard finding type lands in which of
 * the rad's sections) is editable in the Finding routing panel: "Auto-assign
 * with AI" omits the map so the BE seeds it via an LLM; otherwise Save sends
 * the panel's map and the BE persists it verbatim. Revert deletes the row and
 * falls back to the global template.
 *
 * Notable affordances:
 *   - Right-click a section header to Rename or Remove it (shared with the
 *     case viewer via useSectionContextMenu; the label is inert on left-click).
 *   - "Add section" at the bottom inserts a new organSubsection node;
 *     the slug is generated from the typed label via `kebabify`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Editor } from "@tiptap/react";
import { DOMParser as PMDOMParser } from "prosemirror-model";

import { FaSpinner } from "../utils/icons";
import { useTemplate, useTemplates } from "../hooks/useTemplate";
import {
  useDeleteUserTemplate,
  useUpsertUserTemplate,
  useUserTemplate,
} from "../hooks/useRadTemplate";
import { TemplateEditor, extractTemplateStructure } from "./TemplateEditor";
import { buildReportDoc } from "../utils/reportUtils";
import { kebabify } from "../utils/slug";

const MyTemplates: React.FC = () => {
  const navigate = useNavigate();

  const catalogQuery = useTemplates();
  const [examType, setExamType] = useState<string | null>(null);
  useEffect(() => {
    if (!examType && catalogQuery.data?.length) {
      setExamType(catalogQuery.data[0].examType);
    }
  }, [catalogQuery.data, examType]);

  const globalQuery = useTemplate(examType);
  const userQuery = useUserTemplate(examType);
  const upsertMutation = useUpsertUserTemplate();
  const deleteMutation = useDeleteUserTemplate();
  const editorRef = useRef<Editor | null>(null);
  const [statusMessage, setStatusMessage] = useState<{
    kind: "saved" | "reverted";
    text: string;
  } | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  // The rad's explicit routing overrides, keyed by standard section kind. The
  // displayed value falls back to the saved routingMap when a kind isn't
  // overridden, so the panel is always pre-filled without a seeding effect.
  const [routingOverrides, setRoutingOverrides] = useState<Record<string, string | null>>({});

  // The editor renders the user's template if saved, else the global default.
  // Synthesize a ReportTemplate shape from the user's saved sections so the
  // TipTap initial content is consistent (id/version are display-irrelevant).
  const effectiveTemplate = useMemo(() => {
    if (userQuery.data) {
      const global = globalQuery.data;
      return {
        id: global?.id ?? userQuery.data.examType,
        version: global?.version ?? "user",
        examType: userQuery.data.examType,
        negativeImpression: global?.negativeImpression ?? "",
        findings: userQuery.data.sections,
      };
    }
    return globalQuery.data;
  }, [globalQuery.data, userQuery.data]);

  const errorMsg =
    (catalogQuery.error as Error | undefined)?.message ??
    (globalQuery.error as Error | undefined)?.message ??
    (userQuery.error as Error | undefined)?.message ??
    (upsertMutation.error as Error | undefined)?.message ??
    (deleteMutation.error as Error | undefined)?.message ??
    null;

  const handleEditorReady = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  // Standard report sections (the routing-source rows) and the rad's sections
  // (the dropdown targets). Routing maps the former onto the latter.
  const standardSections = globalQuery.data?.findings ?? [];
  const savedRouting = userQuery.data?.routingMap;
  const userSectionOptions = effectiveTemplate?.findings ?? [];

  // Displayed routing for a standard section: the rad's override if present,
  // else the saved value, else "no section" (null).
  const routingFor = useCallback(
    (standardKind: string): string | null =>
      standardKind in routingOverrides
        ? routingOverrides[standardKind]
        : (savedRouting?.[standardKind] ?? null),
    [routingOverrides, savedRouting],
  );

  const handleSave = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor || !examType) return;
    const sections = extractTemplateStructure(editor);
    if (sections.length === 0) {
      setAddError("Template needs at least one section before saving.");
      return;
    }
    // Once a routing map exists (or the rad has overridden a row), persist the
    // panel verbatim — no LLM. A first, untouched save omits routingMap so the
    // backend seeds it with the LLM's guess.
    const hasRouting = Object.keys(routingOverrides).length > 0 || savedRouting !== undefined;
    const routingMap = hasRouting
      ? Object.fromEntries(standardSections.map((s) => [s.kind, routingFor(s.kind)]))
      : undefined;
    await upsertMutation.mutateAsync({ examType, sections, routingMap });
    setRoutingOverrides({});
    setStatusMessage({ kind: "saved", text: "Template saved." });
  }, [upsertMutation, routingOverrides, savedRouting, standardSections, routingFor, examType]);

  const handleAutoAssign = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor || !examType) return;
    const sections = extractTemplateStructure(editor);
    if (sections.length === 0) {
      setAddError("Template needs at least one section before assigning routing.");
      return;
    }
    // Omit routingMap -> backend regenerates it via the LLM.
    await upsertMutation.mutateAsync({ examType, sections });
    setRoutingOverrides({});
    setStatusMessage({ kind: "saved", text: "AI assigned finding routing." });
  }, [upsertMutation, examType]);

  const handleRevert = useCallback(async () => {
    if (!examType) return;
    await deleteMutation.mutateAsync(examType);
    const editor = editorRef.current;
    const global = globalQuery.data;
    if (editor && global) {
      editor.commands.setContent(buildReportDoc(global, {}, {}, []));
    }
    setRoutingOverrides({});
    setStatusMessage({ kind: "reverted", text: "Reverted to global default." });
  }, [deleteMutation, globalQuery.data, examType]);

  const handleAddSection = useCallback(() => {
    setAddError(null);
    const editor = editorRef.current;
    if (!editor) return;
    let kind: string;
    try {
      kind = kebabify(newLabel);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Invalid label");
      return;
    }
    const existing = extractTemplateStructure(editor);
    if (existing.some((s) => s.kind === kind)) {
      setAddError(`Label resolves to slug "${kind}" which is already used.`);
      return;
    }
    // Insert a new organSubsection at the end of findingsSection. Find the
    // section node and append after its last child. The schema's
    // FieldNode is atomic with text on attrs.text; emit a "template"-kind
    // chip with empty default text — the rad fills it in next.
    const doc = editor.state.doc;
    let findingsPos: number | null = null;
    let findingsEnd: number | null = null;
    doc.descendants((node, pos) => {
      if (node.type.name === "findingsSection") {
        findingsPos = pos;
        findingsEnd = pos + node.nodeSize - 1;
        return false;
      }
      return undefined;
    });
    if (findingsPos === null || findingsEnd === null) {
      setAddError("Could not locate FINDINGS section in template.");
      return;
    }
    const trimmedLabel = newLabel.trim();
    const labelForDisplay = trimmedLabel.toUpperCase();
    const view = editor.view;
    const { schema } = view.state;
    const doc_ = view.dom.ownerDocument;
    const wrapper = doc_.createElement("div");
    const section = doc_.createElement("div");
    section.setAttribute("data-organ-subsection", "true");
    section.setAttribute("data-kind", kind);
    section.setAttribute("data-label", labelForDisplay);
    const p = doc_.createElement("p");
    const span = doc_.createElement("span");
    span.setAttribute("data-kind", "template");
    span.setAttribute("data-text", "");
    p.appendChild(span);
    section.appendChild(p);
    wrapper.appendChild(section);
    const slice = PMDOMParser.fromSchema(schema).parseSlice(wrapper);
    view.dispatch(view.state.tr.insert(findingsEnd, slice.content));
    setNewLabel("");
    setAddingSection(false);
  }, [newLabel]);

  const isLoading = catalogQuery.isLoading || globalQuery.isLoading || userQuery.isLoading;
  const isPending = upsertMutation.isPending || deleteMutation.isPending;
  const hasSaved = !!userQuery.data;
  // Save builds the routing map from the global template's standard sections.
  // Without globalQuery.data those rows are empty, so an "explicit" save would
  // post an all-null map and wipe the rad's saved routing backend-side. Block
  // Save until the global template loads (e.g. while it's fetching or errored).
  const canSave = !!examType && !!effectiveTemplate && !!globalQuery.data;
  const examLabel = globalQuery.data?.displayName ?? examType ?? "Report template";

  return (
    <div className="min-h-screen bg-bg-2 text-white">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-signal-text-tint mb-1">
              My Templates
            </p>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">{examLabel}</h1>
              {catalogQuery.data && catalogQuery.data.length > 1 && (
                <select
                  value={examType ?? ""}
                  onChange={(event) => {
                    editorRef.current = null;
                    setRoutingOverrides({});
                    setStatusMessage(null);
                    setExamType(event.target.value || null);
                  }}
                  className="text-xs bg-surface-2 border border-border-strong rounded px-2 py-1 text-white"
                  data-testid="my-templates-exam-type"
                >
                  {catalogQuery.data.map((entry) => (
                    <option key={entry.examType} value={entry.examType}>
                      {entry.displayName ?? entry.examType}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-xs text-fg-muted hover:text-white px-3 py-1.5 rounded border border-border-strong hover:border-signal-stroke transition-colors"
              data-testid="my-templates-back"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleRevert}
              disabled={isPending || !hasSaved}
              className="text-xs text-fg-muted hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded border border-border-strong hover:border-border-strong transition-colors"
              data-testid="my-templates-revert"
            >
              Revert to global
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || !canSave}
              className="bg-signal hover:bg-signal disabled:bg-surface-2 disabled:cursor-not-allowed text-white text-xs font-medium px-4 py-1.5 rounded transition-colors"
              data-testid="my-templates-save"
            >
              {upsertMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <FaSpinner className="animate-spin" />
                  {/* The LLM only runs when the payload omits routingMap (first
                      save / Auto-assign). A verbatim map skips it. */}
                  {upsertMutation.variables?.routingMap === undefined
                    ? "Saving and mapping AI routing…"
                    : "Saving…"}
                </span>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>

        <p className="text-sm text-fg-muted mb-4">
          Click a section heading to rename or remove it. Edit chip text to override the default, or
          right-click a chip to add or remove findings. Use the Finding routing panel below to
          control where each finding type lands - Auto-assign with AI fills it in, and you can
          override any row.
        </p>

        {errorMsg && (
          <div
            role="alert"
            data-testid="my-templates-error"
            className="mb-4 p-3 bg-warn-tint border border-warn rounded-lg"
          >
            <p className="text-sm text-warn">{errorMsg}</p>
          </div>
        )}

        {statusMessage && !errorMsg && (
          <div
            role="status"
            data-testid={`my-templates-${statusMessage.kind}`}
            className={
              statusMessage.kind === "saved"
                ? "mb-4 p-3 bg-signal-tint border border-signal-stroke rounded-lg"
                : "mb-4 p-3 bg-surface-2 border border-border-strong rounded-lg"
            }
          >
            <p
              className={
                statusMessage.kind === "saved"
                  ? "text-sm text-signal-text-tint"
                  : "text-sm text-fg-muted"
              }
            >
              {statusMessage.text}
            </p>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-fg-muted">
            <FaSpinner className="animate-spin" />
            <span>Loading template…</span>
          </div>
        )}

        {!isLoading && effectiveTemplate && (
          <>
            <TemplateEditor
              key={examType}
              template={effectiveTemplate}
              onReady={handleEditorReady}
            />

            <div className="mt-4">
              {addingSection ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={newLabel}
                    onChange={(e) => {
                      setNewLabel(e.target.value);
                      setAddError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSection();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setAddingSection(false);
                        setNewLabel("");
                        setAddError(null);
                      }
                    }}
                    placeholder="Section label, e.g. INCIDENTAL FINDINGS"
                    className="text-sm px-3 py-1.5 rounded border border-border-strong bg-surface-2 text-white focus:outline-none focus:border-signal-stroke"
                    data-testid="my-templates-new-section-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="bg-signal hover:bg-signal text-white text-xs font-medium px-3 py-1.5 rounded"
                    data-testid="my-templates-confirm-add-section"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAddingSection(false);
                      setNewLabel("");
                      setAddError(null);
                    }}
                    className="text-xs text-fg-muted hover:text-white px-3 py-1.5 rounded border border-border-strong"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingSection(true)}
                  className="text-xs text-signal-text-tint hover:text-fg px-3 py-1.5 rounded border border-signal-stroke-soft hover:border-signal-stroke"
                  data-testid="my-templates-add-section"
                >
                  + Add section
                </button>
              )}
              {addError && (
                <p className="mt-2 text-xs text-warn" data-testid="my-templates-add-section-error">
                  {addError}
                </p>
              )}
            </div>

            <div
              className="mt-8 border-t border-border-strong pt-5"
              data-testid="my-templates-routing-panel"
            >
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-semibold">Finding routing</h2>
                <button
                  type="button"
                  onClick={handleAutoAssign}
                  disabled={isPending}
                  className="text-xs text-signal-text-tint hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded border border-signal-stroke-soft hover:border-signal-stroke"
                  data-testid="my-templates-auto-assign"
                >
                  Auto-assign with AI
                </button>
              </div>
              <p className="text-xs text-fg-muted mb-3">
                Where should each finding type go in your template? Findings left as “Use default
                section” appear under a2z's default heading for that organ system.
              </p>
              <div className="space-y-1.5">
                {standardSections.map((s) => (
                  <div
                    key={s.kind}
                    className="flex items-center justify-between gap-3"
                    data-testid={`routing-row-${s.kind}`}
                  >
                    <span className="text-sm text-fg-muted">{s.label}</span>
                    <select
                      value={routingFor(s.kind) ?? ""}
                      onChange={(e) =>
                        setRoutingOverrides((prev) => ({
                          ...prev,
                          [s.kind]: e.target.value || null,
                        }))
                      }
                      className="text-sm bg-surface-2 border border-border-strong rounded px-2 py-1 text-white focus:outline-none focus:border-signal-stroke min-w-[16rem]"
                      data-testid={`routing-select-${s.kind}`}
                    >
                      <option value="">Use default section</option>
                      {userSectionOptions.map((u) => (
                        <option key={u.kind} value={u.kind}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyTemplates;
